<template>
  <form autocomplete="off" class="setoutForm regular-form-width">
    <atom-info-box>
      An email will be sent to each new email address you have added when your details are saved. Please follow the instructions in the email to verify that you have access to the email address from your account.
    </atom-info-box>

    <molecule-add-a-field
      v-slot="{ index }"
      :model-value="emailAddressEntries"
      label="Email Address"
      width="two-thirds"
      :error-list="validationErrors?.additionalEmails">
      <additional-email-item v-model:additional-email="emailAddressEntries[index]"
                             :index="index"
                             :validation-error="validationErrors?.additionalEmails?.at(index)" />
    </molecule-add-a-field>

    <atom-select id="default-internal" v-model="defaultInternalOption" :options="selectorOptions" label="Default Internal"
                 :label-extractor="option => option.emailAddress" :error-message="validationErrors?.defaultInternal"/>
    <atom-select id="default-external" v-model="defaultExternalOption" :options="selectorOptions" label="Default External"
                 :label-extractor="option => option.emailAddress" :error-message="validationErrors?.defaultExternal"/>
  </form>
</template>

<script lang="ts">
import type { AdditionalEmail } from "authentication/js/api/user-account.api";
import type { UserDetailValidationErrors } from "roles/components/user/EditUserDetails.vue";
import type { PropType } from "vue";
import AdditionalEmailItem from "authentication/components/mydetails/AdditionalEmailItem.vue";
import AtomInfoBox from "core/components/atom/infoBox/AtomInfoBox.vue";
import AtomSelect from "core/components/atom/select/AtomSelect.vue";
import MoleculeAddAField from "core/components/molecule/addAField/MoleculeAddAField.vue";
import { defineComponent } from "vue";

export default defineComponent({
  components: { AdditionalEmailItem, AtomSelect, MoleculeAddAField, AtomInfoBox },
  props: {
    emailAddresses: { type: Array as PropType<AdditionalEmail[]>, required: true },
    defaultInternal: { type: String as PropType<string> },
    defaultExternal: { type: String as PropType<string> },
    validationErrors: { type: Object as PropType<UserDetailValidationErrors> },
  },
  emits: {
    "update:defaultInternal": (_: string | undefined) => true,
    "update:defaultExternal": (_: string | undefined) => true,
    "update:emailAddresses": (_: AdditionalEmail[]) => true,
  },
  computed: {
    emailAddressEntries: {
      get() {
        return this.emailAddresses;
      },
      set(entries: AdditionalEmail[]) {
        this.$emit("update:emailAddresses", entries);
      },
    },
    defaultInternalOption: {
      get() {
        return this.selectorOptions.find(option => option.emailAddress === this.defaultInternal);
      },
      set(option: AdditionalEmail) {
        this.$emit("update:defaultInternal", option.emailAddress);
      },
    },
    defaultExternalOption: {
      get() {
        return this.selectorOptions.find(option => option.emailAddress === this.defaultExternal);
      },
      set(option: AdditionalEmail) {
        this.$emit("update:defaultExternal", option.emailAddress);
      },
    },
    selectorOptions() {
      return this.emailAddresses.filter(emailAddress => emailAddress.emailAddress);
    },
  },
  watch: {
    emailAddresses: {
      handler(emails) {
        this.removeSelectionsIfInvalid(emails);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    removeSelectionsIfInvalid(emails: AdditionalEmail[]) {
      if (this.defaultInternal && !emails.map(email => email.emailAddress).includes(this.defaultInternal)) {
        this.$emit("update:defaultInternal", undefined);
      }

      if (this.defaultExternal && !emails.map(email => email.emailAddress).includes(this.defaultExternal)) {
        this.$emit("update:defaultExternal", undefined);
      }
    },
  },
});
</script>
